import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';

interface Trophy {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  trophyType: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count: {
    awards: number;
  };
}

interface TrophyFormData {
  name: string;
  description: string;
  trophyType: string;
  isActive: boolean;
  image?: File;
}

const TROPHY_TYPES = {
  custom: 'Custom Trophy',
  monthly_first: '1st Place Monthly',
  monthly_second: '2nd Place Monthly', 
  monthly_third: '3rd Place Monthly',
  annual_first: '1st Place Annual',
  annual_second: '2nd Place Annual',
  annual_third: '3rd Place Annual',
  league_first: '1st Place League',
  league_second: '2nd Place League',
  league_third: '3rd Place League'
};

const AdminTrophies: React.FC = () => {
  const [trophies, setTrophies] = useState<Trophy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTrophy, setEditingTrophy] = useState<Trophy | null>(null);
  const [deleteModalTrophy, setDeleteModalTrophy] = useState<Trophy | null>(null);
  const [formData, setFormData] = useState<TrophyFormData>({
    name: '',
    description: '',
    trophyType: 'custom',
    isActive: true
  });
  const [formSubmitting, setFormSubmitting] = useState(false);

  useEffect(() => {
    fetchTrophies();
  }, []);

  const fetchTrophies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/trophies/admin/all');
      setTrophies(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load trophies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      description: '',
      trophyType: 'custom',
      isActive: true
    });
    setEditingTrophy(null);
    setShowCreateModal(true);
  };

  const handleEdit = (trophy: Trophy) => {
    setFormData({
      name: trophy.name,
      description: trophy.description,
      trophyType: trophy.trophyType,
      isActive: trophy.isActive
    });
    setEditingTrophy(trophy);
    setShowCreateModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setFormSubmitting(true);
      
      const formDataObj = new FormData();
      formDataObj.append('name', formData.name);
      formDataObj.append('description', formData.description);
      formDataObj.append('trophyType', formData.trophyType);
      formDataObj.append('isActive', formData.isActive.toString());
      
      if (formData.image) {
        formDataObj.append('image', formData.image);
      }

      if (editingTrophy) {
        await apiClient.put(`/trophies/admin/${editingTrophy.id}`, formDataObj, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await apiClient.post('/trophies/admin', formDataObj, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      setShowCreateModal(false);
      setEditingTrophy(null);
      fetchTrophies();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to save trophy');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async (trophy: Trophy) => {
    try {
      await apiClient.delete(`/trophies/admin/${trophy.id}`);
      setDeleteModalTrophy(null);
      fetchTrophies();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete trophy');
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, image: file });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highlight"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Trophy Management
            </h1>
            <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
              Create and manage trophies for user achievements
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="btn-primary px-4 py-2"
          >
            Create Trophy
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 mb-6">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Trophies Table */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Trophy
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Awards
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {trophies.map((trophy) => (
              <tr key={trophy.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {trophy.imageUrl && (
                      <img
                        src={trophy.imageUrl}
                        alt={trophy.name}
                        className="h-10 w-10 rounded-full mr-4 object-cover"
                      />
                    )}
                    <div>
                      <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {trophy.name}
                      </div>
                      <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {trophy.description.length > 50 
                          ? `${trophy.description.substring(0, 50)}...` 
                          : trophy.description}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    {TROPHY_TYPES[trophy.trophyType as keyof typeof TROPHY_TYPES] || trophy.trophyType}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium" style={{ color: 'var(--highlight)' }}>
                    {trophy._count.awards}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    trophy.isActive
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {trophy.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {new Date(trophy.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleEdit(trophy)}
                    className="btn-secondary px-3 py-1 text-sm mr-2"
                  >
                    Edit
                  </button>
                  <Link
                    to={`/admin/trophies/${trophy.id}/awards`}
                    className="btn-primary px-3 py-1 text-sm mr-2"
                  >
                    Awards
                  </Link>
                  <button
                    onClick={() => setDeleteModalTrophy(trophy)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {trophies.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              No trophies
            </h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
              Get started by creating a new trophy.
            </p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              {editingTrophy ? 'Edit Trophy' : 'Create Trophy'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                  style={{ color: 'var(--text-primary)' }}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                  style={{ color: 'var(--text-primary)' }}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  Trophy Type
                </label>
                <select
                  value={formData.trophyType}
                  onChange={(e) => setFormData({ ...formData, trophyType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {Object.entries(TROPHY_TYPES).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  Trophy Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                />
                {editingTrophy?.imageUrl && (
                  <img 
                    src={editingTrophy.imageUrl} 
                    alt="Current trophy" 
                    className="mt-2 h-16 w-16 object-cover rounded"
                  />
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="isActive" className="text-sm" style={{ color: 'var(--text-primary)' }}>
                  Active
                </label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary px-4 py-2"
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary px-4 py-2"
                  disabled={formSubmitting}
                >
                  {formSubmitting ? 'Saving...' : editingTrophy ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalTrophy && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Delete Trophy
            </h3>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Are you sure you want to delete "{deleteModalTrophy.name}"? This will also remove all trophy awards.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setDeleteModalTrophy(null)}
                className="btn-secondary px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteModalTrophy)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTrophies;